// ── participant-assignments-collection: bounded client adapter (no urlSync) ───
// Per-week assignment summary rows for the participant dashboard Assignments
// section. Own-cohort scope only — collection state stays in-memory (urlSync:false).

import { useMemo } from "react";
import {
  Badge,
  useCollection,
  type CollectionColumnDef,
  type UseCollectionResult
} from "@vjal/ui";

export const PARTICIPANT_ASSIGNMENTS_PAGE_SIZE = 25;
export const PARTICIPANT_ASSIGNMENTS_DEFAULT_SORT = { id: "weekNumber", dir: "asc" as const };

export type ParticipantAssignmentRow = {
  id: string;
  week: string;
  weekNumber: number;
  submitted: string;
};

export function buildParticipantAssignmentsCollectionColumns(): CollectionColumnDef<ParticipantAssignmentRow>[] {
  return [
    {
      id: "week",
      header: "Week",
      sortable: true,
      sortAccessor: (row) => row.weekNumber,
      searchValue: (row) => row.week,
      priority: 1,
      cell: (row) => <span className="font-medium text-strong">{row.week}</span>
    },
    {
      id: "submitted",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.submitted,
      priority: 2,
      cell: (row) => (
        <Badge variant={row.submitted === "submissions recorded" ? "success" : "default"}>{row.submitted}</Badge>
      )
    }
  ];
}

export function useParticipantAssignmentsCollection(
  rows: ParticipantAssignmentRow[]
): UseCollectionResult<ParticipantAssignmentRow> {
  const columns = useMemo(() => buildParticipantAssignmentsCollectionColumns(), []);

  return useCollection<ParticipantAssignmentRow>({
    mode: "client",
    rows,
    columns,
    pageSize: PARTICIPANT_ASSIGNMENTS_PAGE_SIZE,
    urlSync: false,
    initialState: { sort: PARTICIPANT_ASSIGNMENTS_DEFAULT_SORT },
    getRowKey: (row) => row.id
  });
}